/**
 * useAuth Hook
 * 
 * Provides authentication state and methods for managing user sessions.
 * Uses Supabase Auth under the hood.
 */

import { useState, useEffect } from 'react'
import { getCurrentUser, onAuthStateChange, signOut, signIn as authSignIn, signUp as authSignUp, signInWithMagicLink as authMagicLink, updatePassword, requestResetEmail } from '../services/auth'
import posthog from 'posthog-js'

/**
 * Custom hook for authentication
 * @returns {Object} { user, loading, signOut: function }
 */
export const useAuth = () => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Get initial user
    const initAuth = async () => {
      try {
        const currentUser = await getCurrentUser()
        setUser(currentUser)
      } catch (error) {
        console.error('Error initializing auth:', error)
        setUser(null)
      } finally {
        setLoading(false)
      }
    }

    initAuth()

    // Subscribe to auth state changes
    const unsubscribe = onAuthStateChange((user) => {
      setUser(user)
      setLoading(false)
      // Re-identify on page refresh if the user is already logged in
      if (user) {
        posthog.identify(user.id, { tier: user.user_metadata?.tier })
      }
    })

    return () => {
      unsubscribe()
    }
  }, [])

  const handleSignOut = async () => {
    try {
      const { error } = await signOut()
      if (error) {
        console.error('Error signing out:', error)
        return { error }
      }
      posthog.capture('user_signed_out')
      posthog.reset()
      setUser(null)
      return { error: null }
    } catch (error) {
      console.error('Error signing out:', error)
      return { error }
    }
  }

  const handleUpdatePassword = async (newPassword) => {
    const result = await updatePassword(newPassword)
    return { success: !result.error, error: result.error }
  }

  const handleSignIn = async (email, password) => {
    const result = await authSignIn(email, password)
    return { success: !result.error, error: result.error }
  }

  const handleSignUp = async (email, password) => {
    const result = await authSignUp(email, password)
    return { success: !result.error, error: result.error }
  }

  const handleSignInWithMagicLink = async (email) => {
    const result = await authMagicLink(email)
    return { success: !result.error, error: result.error }
  }

  const resetPassword = async (email) => {
    const redirectTo = typeof window !== 'undefined' ? `${window.location.origin}/auth/reset-callback` : undefined
    return await requestResetEmail(email, redirectTo)
  }

  return {
    user,
    loading,
    isAuthenticated: !!user,
    signOut: handleSignOut,
    updatePassword: handleUpdatePassword,
    signIn: handleSignIn,
    signUp: handleSignUp,
    signInWithMagicLink: handleSignInWithMagicLink,
    resetPassword,
  }
}
